import { useEffect } from 'react';

import { initBirdie } from '@birdie-so/snippet';

import { getAppGlobals } from '~/components/AppGlobals/utils';

// Module-level variables to track initialization and previous values
let birdieInitialized = false;
let lastPublicationId: string | undefined;

interface BirdieLoaderProps {
  publication: {
    id: string;
  };
  isAdmin: boolean;
}

const BirdieLoader = ({ publication, isAdmin }: BirdieLoaderProps) => {
  const appGlobals = getAppGlobals();
  const birdieClientId = appGlobals.VITE_BIRDIE_CLIENT_ID;

  useEffect(() => {
    if (!isAdmin) return;

    // Defer Birdie initialization until after hydration completes
    // Use requestIdleCallback to load during browser idle time, or setTimeout as fallback
    const scheduleLoad = () => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(() => loadBirdie());
      } else {
        setTimeout(() => loadBirdie(), 1);
      }
    };

    const loadBirdie = () => {
      if (!birdieClientId) return;

      // Only initialize once across all component instances
      if (!birdieInitialized) {
        initBirdie({
          clientId: birdieClientId,
          metadata: {
            publication: {
              id: publication.id,
            },
          },
        });

        // Store initial values
        lastPublicationId = publication.id;
        birdieInitialized = true;
      } else {
        // Only update metadata if values have changed
        const hasPublicationChanged = lastPublicationId !== publication.id;

        if (hasPublicationChanged) {
          // Update Snippet metadata if available
          if ((window as any).birdie && typeof (window as any).birdie.metadata !== 'undefined') {
            (window as any).birdie.metadata = {
              publication: {
                id: publication.id,
              },
            };
          }

          // Update stored values
          lastPublicationId = publication.id;
        }
      }
    };

    scheduleLoad();
  }, [birdieClientId, publication.id, isAdmin]);

  return null;
};

export default BirdieLoader;
